import {
	useTenantSSOMappings,
	useUpdateTenantSSOGroupMappings as updateTenantSSOMappings
} from '@api/authMethods';
import { useItems } from '@api/generics';
import QueryKeys from '@api/index';
import API_ROUTES from '@api/routes';
import { ActionsFooter, ApprovalDialogButton } from '@components/Forms';
import Loader from '@components/Loader/Loader';
import { LoadingButton } from '@mui/lab';
import { Box, Divider, Grid, Typography, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import { noop, sortBy } from 'lodash';
import React, { FC } from 'react';
import { IRole } from 'types/authorization';
import { ISSOGroupMappingsItem, ISSOMappings } from 'types/settings';
import { ITenant } from 'types/tenants';
import AttributeMappings from '../AttributeMappings/AttributeMappings';
import SSOMappings from './SSOMappings';

interface ISSOMappingsPageProps {
	tenant?: ITenant;
}

const SSOMappingsPage: FC<ISSOMappingsPageProps> = ({ tenant }) => {
	const { invokeToastableAction } = useToast();
	const { data, isLoading: isSSOMappingsLoading } = useTenantSSOMappings(
		tenant?.id || ''
	);

	const ssoGroupsMappingsMutation = updateTenantSSOMappings(tenant?.id || '');
	const { data: rolesData, isLoading } = useItems<IRole>(
		QueryKeys.roles,
		API_ROUTES.v1.roles.all
	);
	const roles: IRole[] = React.useMemo(
		() => sortBy(rolesData?.data || [], (v) => v.name.toLowerCase()),
		[rolesData]
	);
	const hasRoles = !isLoading && roles.length;

	const formik = useFormik<ISSOMappings>({
		initialValues: {
			samlMappings: data?.samlMappings || {},
			mappings: (data?.mappings || []).map(
				(mapping: ISSOGroupMappingsItem) => ({
					...mapping
				})
			)
		},
		enableReinitialize: true,
		onSubmit: async (values) => {
			await invokeToastableAction(
				() => ssoGroupsMappingsMutation.mutateAsync(values),
				'tenants.sso.mappings.groups.dialog',
				noop
			);
		}
	});

	return (
		<Box
			display="flex"
			flexDirection="column"
			flexGrow={1}
			data-testid="sso-mappings-page"
			component="form"
			overflow="hidden"
			height="100%"
		>
			<Loader isLoading={isLoading || isSSOMappingsLoading} variant="settings">
				<>
					<Box overflow="auto" flexGrow={1} paddingLeft={3}>
						<Grid container>
							<Grid item xs={12}>
								<Grid item mb={1}>
									<Typography variant="overline" color="text.secondary">
										{intl.tr('authMethods.SSO.user.mapping.title')}
									</Typography>
									<Typography mt={3} variant="body2" color="text.primary">
										{intl.tr('authMethods.SSO.user.mapping.secondary.title')}
									</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Box pt={2}>
							<AttributeMappings formik={formik} />
						</Box>

						<Grid container spacing={2} pt={4} pb={4}>
							<Grid item xs={12} component={Divider} />
						</Grid>

						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography variant="overline" color="text.secondary">
									{intl.tr('authMethods.SSO.group.mapping.title')}
								</Typography>
							</Grid>
						</Grid>

						{hasRoles ? (
							<Grid container spacing={2} mt={2}>
								<Grid item xs={12}>
									<Grid item xs mb={2}>
										<Typography variant="body2" color="text.primary">
											{intl.tr(
												'authMethods.SSO.group.mapping.description.label'
											)}
											<br />
											{intl.tr(
												'authMethods.SSO.group.mapping.description.sublabel'
											)}
										</Typography>
									</Grid>
								</Grid>
								<Grid item sm={4} lg={4}>
									<TextField
										fullWidth
										inputProps={{
											'data-testid': 'idp-group-attr-field'
										}}
										defaultValue={data?.samlMappings?.group}
										onChange={(e) => {
											formik.setFieldValue('samlMappings', {
												...data?.samlMappings,
												group: e.target.value
											});
										}}
										label={intl.tr(
											'authMethods.SSO.group.mapping.attributes.field.attribute.label'
										)}
										placeholder={intl.tr(
											'authMethods.SSO.group.mapping.attributes.field.attribute.placeholder'
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Typography variant="body2" color="text.primary">
										{intl.tr('authMethods.SSO.group.mapping.cta.label')}
									</Typography>
								</Grid>
								<Grid item xs={12}>
									<SSOMappings formik={formik} id="mappings" roles={roles} />
								</Grid>
							</Grid>
						) : (
							<Box pl={2} pt={3}>
								<Typography variant="body2" color="text.primary">
									{intl.tr(
										'tenants.sso.mappings.groups.dialog.no.roles.message'
									)}
								</Typography>
							</Box>
						)}
					</Box>
					<ActionsFooter>
						<LoadingButton
							color="primary"
							variant="contained"
							loading={formik.isSubmitting}
							disabled={!formik.dirty}
							sx={{ marginRight: 1 }}
							onClick={() => formik.handleSubmit()}
							data-testid="sso-mappings-save-button"
						>
							{intl.tr('button.save')}
						</LoadingButton>
						<ApprovalDialogButton
							buttonText={intl.tr('button.cancel')}
							onApprove={formik.resetForm}
							disabled={!formik.dirty}
							title={intl.tr('discard.changes.dialog.title')}
							question={intl.tr('discard.changes.dialog.question')}
							approveText={intl.tr('discard.changes.dialog.approve')}
						/>
					</ActionsFooter>
				</>
			</Loader>
		</Box>
	);
};

SSOMappingsPage.defaultProps = {
	tenant: undefined
};

export default SSOMappingsPage;
